/**
 * Dashboard (Home) Page Component
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, Droplets, Tv, Monitor, History, RefreshCw, ChevronRight, Sparkles } from 'lucide-react';
import { IMAGES } from '../lib/marketingImages';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import TransactionItem from '../components/TransactionItem';
import { ErrorState } from '../components/ErrorState';
import { classifyError } from '../lib/apiError';
import { API_URL, BACKEND_URL } from '../config';


const Dashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API_URL}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
      setError(classifyError(err));
    } finally {
      setLoading(false);
    }
  };

  const hour = new Date().getHours();
  const hello = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.full_name ? user.full_name.split(' ')[0] : '';

  const quickActions = [
    { icon: Zap, label: t("electricity"), color: "bg-yellow-500", path: "/buy?provider=ESCOM", photo: IMAGES.heroPower },
    { icon: Droplets, label: t("water"), color: "bg-cyan-500", path: "/buy?provider=LWB", photo: IMAGES.water },
    { icon: Tv, label: "DStv", color: "bg-blue-600", path: "/buy?provider=DSTV", photo: IMAGES.tv },
    { icon: Monitor, label: "GOtv", color: "bg-green-600", path: "/buy?provider=GOTV", photo: IMAGES.livingRoom },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 loading-shimmer rounded" />
        <div className="bento-grid">
          <div className="bento-large h-64 loading-shimmer rounded-xl" />
          <div className="h-32 loading-shimmer rounded-xl" />
          <div className="h-32 loading-shimmer rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchStats} />;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{hello}{firstName ? `, ${firstName}` : ''}!</h1>
        <p className="text-muted-foreground">What should we keep running at home today?</p>
      </div>

      <button
        type="button"
        onClick={() => navigate('/buy')}
        className="relative w-full overflow-hidden rounded-2xl text-left group"
      >
        <img src={IMAGES.heroHome} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F2E2B]/90 to-[#226D64]/55" />
        <div className="relative z-10 p-5 sm:p-6 text-white flex items-center justify-between gap-4">
          <div>
            <p className="text-[#99E1D9] text-xs font-semibold uppercase tracking-wider mb-1 inline-flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> 30-second pay
            </p>
            <p className="text-xl font-bold">Need units? Pay a bill now.</p>
            <p className="text-white/80 text-sm mt-1">Electricity, water, or TV — tokens land on your phone.</p>
          </div>
          <span className="hidden sm:inline-flex px-4 py-2 bg-[#99E1D9] text-[#0F2E2B] rounded-xl font-semibold shrink-0">
            Pay now
          </span>
        </div>
      </button>

      {/* Expiring Subscriptions Alert */}
      {stats?.expiring_subscriptions?.length > 0 && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4" data-testid="expiring-alert">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <RefreshCw className="w-5 h-5 text-orange-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-orange-500">Subscription Renewal Reminder</h3>
              <div className="mt-2 space-y-2">
                {stats.expiring_subscriptions.map((sub, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <Tv className="w-4 h-4 text-blue-500" />
                      {sub.provider_code} - {sub.account_number}
                    </span>
                    <span className={`font-medium ${sub.days_left <= 1 ? "text-red-500" : "text-orange-500"}`}>
                      {sub.days_left === 0 ? "Expires today!" : `${sub.days_left} days left`}
                    </span>
                  </div>
                ))}
              </div>
              <button onClick={() => navigate("/buy?provider=DSTV")} className="mt-3 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-all">
                Renew Now
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bento-grid">
        {/* Balance Card */}
        <div className="bento-large welcome-card-gradient rounded-2xl p-6 text-white" data-testid="balance-card">
          <p className="text-white/80 mb-2">You've kept home running with</p>
          <h2 className="text-4xl font-bold balance-display mb-4">MWK {(stats?.total_spent || 0).toLocaleString()}</h2>
          <div className="flex items-center gap-4 text-sm">
            <div>
              <p className="text-white/70">Bills paid</p>
              <p className="text-2xl font-semibold">{stats?.total_tokens || 0}</p>
            </div>
            <div>
              <p className="text-white/70">Saved meters</p>
              <p className="text-2xl font-semibold">{stats?.saved_accounts || 0}</p>
            </div>
          </div>
        </div>

        {quickActions.map((action, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => navigate(action.path)}
            className="relative overflow-hidden rounded-xl min-h-[7.5rem] text-left group cursor-pointer border border-border"
            data-testid={`quick-action-${action.label.toLowerCase()}`}
          >
            <img src={action.photo} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-[#0F2E2B]/55 group-hover:bg-[#0F2E2B]/40 transition-colors" />
            <div className="relative z-10 p-4 h-full flex flex-col justify-end text-white">
              <div className={`quick-action-icon ${action.color} mb-2 w-10 h-10`}>
                <action.icon className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold">{action.label}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Recent Transactions */}
      <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="recent-transactions">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Recent Transactions</h3>
          <button onClick={() => navigate("/history")} className="text-sm text-primary hover:underline flex items-center gap-1">
            View all <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {stats?.recent_transactions?.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_transactions.map((tx) => (
              <TransactionItem key={tx.id} transaction={tx} compact />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Your first bill is waiting — it only takes a moment.</p>
            <button onClick={() => navigate("/buy")} className="mt-2 text-primary hover:underline">Pay your first bill</button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default Dashboard;